// Shared money/price formatting helpers. Used across the Admin Products & Prices and Domain Pricing
// pages (src/app/Admin/...) and the read-only tenant Prices page (src/app/Organization/...). Money is
// a string end-to-end (DECIMAL(10,4), excl. VAT): parseFloat only to format for display — never to
// store or compare as a float.
const PRICE_FORMATTER = new Intl.NumberFormat('fr-BE', { style: 'currency', currency: 'EUR' });

// Formats a DECIMAL(10,4) money string as a fr-BE euro amount. Falls back to the raw string if the
// value isn't a parseable number, so a malformed backend value never renders as "NaN €".
export function formatPrice(value: string): string {
  const n = parseFloat(value);
  return Number.isNaN(n) ? value : PRICE_FORMATTER.format(n);
}

// Formats a computed money amount (e.g. a quantity × unit-price subtotal) as a fr-BE euro amount.
// Display-only: derived amounts are never stored, compared, or sent back to the backend.
export function formatPriceAmount(value: number): string {
  return PRICE_FORMATTER.format(value);
}

// Unit prices keep their sub-cent precision (up to DECIMAL(10,4)'s 4 fraction digits): a per-GB
// rate like "0.0250" must render as "0,025 €", not a rounded "0,03 €" that visibly contradicts the
// qty × unit-price subtotal displayed next to it. Subtotals/totals stay on the 2-digit formatter.
const UNIT_PRICE_FORMATTER = new Intl.NumberFormat('fr-BE', {
  style: 'currency',
  currency: 'EUR',
  minimumFractionDigits: 2,
  maximumFractionDigits: 4,
});

// Formats a DECIMAL(10,4) unit-price string without losing sub-cent precision. Falls back to the
// raw string if the value isn't a parseable number, so a malformed backend value never renders as
// "NaN €".
export function formatUnitPrice(value: string): string {
  const n = parseFloat(value);
  return Number.isNaN(n) ? value : UNIT_PRICE_FORMATTER.format(n);
}

// Upper bound on the fraction digits rendered (the limit every Intl engine accepts). A plain
// shortest-round-trip literal (e.g. the Verda balance) re-formats to its own digits exactly.
const MAX_CURRENCY_FRACTION_DIGITS = 20;

// Formats a decimal money string in an arbitrary ISO 4217 currency (e.g. a provider's account
// balance, which isn't necessarily EUR) as a fr-BE amount. Keeps the currency's own minimum digits
// ("20" → "20,00 €") but never rounds away extra precision the source reported ("19.8765" →
// "19,8765 €"). Falls back to "<amount> <currency>" for an unparseable amount or a currency code
// Intl rejects, so a malformed backend value never renders as "NaN" or throws.
export function formatCurrencyAmount(amount: string, currency: string): string {
  const n = Number(amount);
  if (amount.trim() === '' || !Number.isFinite(n)) {
    return `${amount} ${currency}`;
  }
  const decimals = (amount.split('.')[1] ?? '').replace(/0+$/, '').length;
  try {
    const base = new Intl.NumberFormat('fr-BE', { style: 'currency', currency });
    const { maximumFractionDigits = 2 } = base.resolvedOptions();
    if (decimals <= maximumFractionDigits) {
      return base.format(n);
    }
    return new Intl.NumberFormat('fr-BE', {
      style: 'currency',
      currency,
      maximumFractionDigits: Math.min(decimals, MAX_CURRENCY_FRACTION_DIGITS),
    }).format(n);
  } catch {
    return `${amount} ${currency}`;
  }
}

// Friendly labels for the known billing cadences; unknown values pass through verbatim so a new
// backend cadence never renders as a blank cell.
const RECURRING_LABELS: Record<string, string> = {
  once: 'One-time',
  hourly: 'Hourly',
  monthly: 'Monthly',
  yearly: 'Yearly',
};

// The usage-billed cadence (GPU servers today, our own VMs later). Its price is PER HOUR, while every
// other cadence is priced per period, so the backend refuses any cadence change to or from it and
// refuses it in the Special category (deals can never carry an hourly product).
export const HOURLY_RECURRING = 'hourly';

export function formatRecurring(recurring: string): string {
  return RECURRING_LABELS[recurring] ?? recurring;
}

// Formats a product's DECIMAL(10,4) price for its cadence. An hourly price keeps its sub-cent
// precision (a per-hour rate like "0.0497" must not render as a rounded "0,05 €"); every other
// cadence keeps the 2-digit formatter it has always used.
export function formatProductPrice(value: string, recurring: string): string {
  return recurring === HOURLY_RECURRING ? formatUnitPrice(value) : formatPrice(value);
}

// Backend contract: unit_price is a non-negative DECIMAL(10,4) — up to 6 integer digits and up to 4
// decimal places. Validated client-side to disable Save early; the backend re-validates and is the
// final authority (its field error arrives in data.unit_price).
export const DECIMAL_RE = /^\d{1,6}(\.\d{1,4})?$/;

// Lightly trims a DECIMAL(10,4) string's trailing fraction zeros for display ("16.0000" → "16",
// "5.5000" → "5.5") by pure string manipulation — no float round-trip, so the value stays verbatim.
// Meant for decimal-string QUANTITIES (e.g. SPLA license units), which share the money columns'
// string discipline without being money.
export function trimDecimalZeros(value: string): string {
  if (!value.includes('.')) {
    return value;
  }
  return value.replace(/0+$/, '').replace(/\.$/, '');
}

// A money string with at most 2 fraction digits: `"12"`, `"0.1"`, `"-3.05"`, `"+4.00"`.
const CENTS_RE = /^([+-])?(\d+)(?:\.(\d{1,2}))?$/;

// Parses a 2-decimal money string into integer cents by string manipulation — no float multiply, so
// `"0.29"` is exactly 29. `null` when the value is not such a string.
function toCents(value: string): number | null {
  const match = CENTS_RE.exec(value.trim());
  if (match === null) {
    return null;
  }
  const cents = Number(match[2]) * 100 + Number((match[3] ?? '').padEnd(2, '0'));
  return match[1] === '-' ? -cents : cents;
}

// Exact sum of 2-decimal money strings (e.g. `first_hour_ttc`, `debit_ttc`), added as integer cents
// and returned as a 2-decimal string (`["0.10", "0.2"]` → `"0.30"`, `["-1.05", "0.05"]` → `"-1.00"`).
// Summing floats would drift (0.1 + 0.2), so client-side totals shown to users go through this.
// Returns `null` when any value is not a plain decimal with at most 2 fraction digits — the total is
// then unknowable, and the caller shows it as unavailable rather than a wrong figure. `[]` → `"0.00"`.
export function sumMoney(values: string[]): string | null {
  let total = 0;
  for (const value of values) {
    const cents = toCents(value);
    if (cents === null) {
      return null;
    }
    total += cents;
  }
  const abs = Math.abs(total);
  const fraction = String(abs % 100);
  return `${total < 0 ? '-' : ''}${Math.floor(abs / 100)}.${fraction.length === 1 ? `0${fraction}` : fraction}`;
}

// Compares two 2-decimal money strings exactly (via integer cents): negative when `a < b`, 0 when
// equal, positive when `a > b`; `null` when either is not such a string. Display gating only — the
// backend decides.
export function compareMoney(a: string, b: string): number | null {
  const ca = toCents(a);
  const cb = toCents(b);
  return ca === null || cb === null ? null : ca - cb;
}
